import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, Tag, Calendar, X } from 'lucide-react';
import { Prompt } from '../types';
import { GitHubPromptManager } from '../utils/github';

interface PromptListItem {
  name: string;
  path: string;
  tags: string[];
}

interface PromptListProps {
  manager: GitHubPromptManager;
  onSelectPrompt: (prompt: Prompt) => void;
  onCreateNew: () => void;
  onCompare: () => void;
}

export function PromptList({ manager, onSelectPrompt, onCreateNew, onCompare }: PromptListProps) {
  const [prompts, setPrompts] = useState<PromptListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const promptList = await manager.listPrompts();
      setPrompts(promptList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [manager]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const prompt of prompts) {
      for (const tag of prompt.tags) {
        if (tag.trim()) {
          tagSet.add(tag);
        }
      }
    }
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [prompts]);

  const filteredPrompts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return prompts.filter((prompt) => {
      const matchesSearch =
        !normalizedSearch ||
        prompt.name.toLowerCase().includes(normalizedSearch);

      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.every((tag) => prompt.tags.includes(tag));

      return matchesSearch && matchesTags;
    });
  }, [prompts, searchTerm, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const removeTag = (tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  };

  const clearAllFilters = () => {
    setSelectedTags([]);
    setSearchTerm('');
  };

  const hasActiveFilters = selectedTags.length > 0 || searchTerm.trim().length > 0;

  const handlePromptClick = async (path: string) => {
    try {
      const prompt = await manager.getPrompt(path);
      onSelectPrompt(prompt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadPrompts}
              className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-md"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Prompt Library</h2>
        <div className="flex gap-2"><button onClick={onCompare} className="px-4 py-2 border border-blue-600 text-blue-700 rounded-md">Compare prompts</button><button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          New Prompt
        </button></div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search prompts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {availableTags.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Tag size={16} className="text-gray-500" />
            <span>Filter by tags</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  aria-pressed={isSelected}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-700'
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <span className="text-sm font-medium text-blue-900">Active filters:</span>
          {searchTerm.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 px-2.5 py-1 text-sm text-blue-800">
              Search: "{searchTerm.trim()}"
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="text-blue-600 hover:text-blue-800"
                aria-label="Clear search filter"
              >
                <X size={14} />
              </button>
            </span>
          )}
          {selectedTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 px-2.5 py-1 text-sm text-blue-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-blue-600 hover:text-blue-800"
                aria-label={`Remove ${tag} filter`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="ml-auto text-sm font-medium text-blue-700 hover:text-blue-900 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {filteredPrompts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {prompts.length === 0
              ? 'No prompts found. Create your first prompt!'
              : 'No matching prompts found.'}
          </p>
          {hasActiveFilters && prompts.length > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPrompts.map((prompt) => (
            <div
              key={prompt.path}
              onClick={() => handlePromptClick(prompt.path)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow"
            >
              <h3 className="font-semibold text-gray-900 mb-2">{prompt.name}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar size={14} />
                <span>Modified recently</span>
              </div>
              <div className="flex items-start gap-2 mt-2">
                <Tag size={14} className="text-gray-400 mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-1">
                  {prompt.tags.length > 0 ? (
                    prompt.tags.map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No tags</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
